<template>
    <div
        class="pipeline-step"
        :class="classes"
        @click="() => (!isPaginated ? handleUpdate() : null)"
    >
        <CompassTooltip v-if="!isPaginated" :arrow="false" contentClass="pipeline-step__tooltip">
            <template #content>
                <span v-html="tooltipContent" />
            </template>
            <div class="pipeline-step__wrapper">
                <span
                    v-if="!finished"
                    class="pipeline-step__label typographies-paragraph-small-regular"
                >
                    {{ step.name }}
                </span>
                <CompassIcon v-else icon="check" size="small" />
            </div>
        </CompassTooltip>
        <div v-else class="pipeline-step__wrapper">
            <CompassMenu trigger="hover" size="small" placement="bottom">
                <CompassIconButton icon="ellipsis" size="extra-small" />
                <template #dropdown>
                    <CompassDropdownOption
                        v-for="subStep in step"
                        :key="subStep.id"
                        :label="subStep.name"
                        @itemSelected="handleUpdate(subStep)"
                    />
                </template>
            </CompassMenu>
        </div>
    </div>
</template>
<script>
export default {
    props: {
        step: {
            type: [Object, Array],
            required: true,
        },
        selected: {
            type: Boolean,
            required: true,
        },
        finished: {
            type: Boolean,
            required: true,
        },
        canEdit: {
            type: Boolean,
            required: true,
        },
        isFirst: {
            type: Boolean,
            default: false,
        },
        isLast: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        tooltipContent() {
            return `${this.step.name} <strong>${this.step.probability}%</strong>`;
        },
        classes() {
            return {
                'pipeline-step--selected': this.selected,
                'pipeline-step--finished': this.finished,
                'pipeline-step--updateable': this.canEdit,
                'pipeline-step--paginated': this.isPaginated,
                'pipeline-step--first': this.isFirst,
                'pipeline-step--last': this.isLast,
            };
        },
        isPaginated() {
            return Array.isArray(this.step);
        },
    },
    methods: {
        handleUpdate(step) {
            if (!this.canEdit || this.selected) {
                return;
            }
            this.$emit('update', step ?? this.step);
        },
    },
};
</script>
<style lang="scss">
.pipeline-step__tooltip {
    color: var(--colors-common-text-contrast-caption);

    strong {
        color: var(--colors-common-text-contrast-default);
    }
}
</style>
<style lang="scss" scoped>
.pipeline-step {
    flex: 1;
    min-width: 0;
    padding: 0 0 0 6px;
    position: relative;

    &--paginated {
        flex: 0;
        min-width: initial;
    }

    :deep() {
        .compass-tooltip {
            display: block;
        }
    }

    &:before,
    &:after {
        content: '';
        background-color: var(--colors-common-background-subtle);
        bottom: 0;
        clip-path: polygon(100% 0%, 100% 100%, 0 100%, 75% 50%, 0 0);
        display: block;
        left: 0;
        position: absolute;
        top: 0;
        width: 8px;
        z-index: 2;
    }

    &:after {
        left: calc(100% - 2px);
        clip-path: polygon(25% 0, 100% 50%, 25% 100%, 0% 100%, 0% 0%);
        width: 8px;
    }

    &__wrapper {
        align-items: center;
        background-color: var(--colors-common-background-subtle);
        display: flex;
        justify-content: center;
        padding: var(--spacing-0) 2px;
    }

    &__label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &--updateable {
        cursor: pointer;

        &:hover {
            &:not(.pipeline-step--selected) {
                .pipeline-step__wrapper {
                    background-color: var(--colors-component-background-actionable-hover);
                }

                &:before,
                &:after {
                    background-color: var(--colors-component-background-actionable-hover);
                }
            }

            &.pipeline-step--finished {
                .pipeline-step__wrapper {
                    background-color: var(--colors-common-background-success-subtle);
                    color: var(--colors-common-text-success-hover);
                    justify-content: center;
                }

                &:before,
                &:after {
                    background-color: var(--colors-common-background-success-subtle);
                }
            }
        }
    }

    &--selected {
        .pipeline-step__wrapper {
            background-color: var(--colors-component-background-actionable-active-default);
            color: var(--colors-common-text-standard-on-color);
        }

        &:before,
        &:after {
            background-color: var(--colors-component-background-actionable-active-default);
        }
    }

    &--finished {
        .pipeline-step__wrapper {
            background-color: var(--colors-common-background-success-subtle);
            color: var(--colors-common-text-success-default);
        }

        &:before,
        &:after {
            background-color: var(--colors-common-background-success-subtle);
        }
    }

    &--first {
        padding-left: 0;

        &:before {
            content: none;
        }
    }

    &--last {
        &:after {
            content: none;
        }
    }
}
</style>
